
import { Manager, IManager } from './Manager';
import { EventFieldType, IEvent, IEventMetadata, IEventMetadataDeclarationTable, IEventMetadataTable, IEventContext } from './IEvent';
import { CorrelationVector } from './CorrelationVector';
import { assign } from '@ms/utilities-objects/lib/assign';

// tslint:disable:no-bitwise

let _id = 0;

export enum ClonedEventType {
    Single,
    Start,
    End
}

export enum ValidationErrorType {
    NoParent = 1 << 0
}

export enum AccountType {
    Consumer,
    ConsumerAnonymous,
    Business,
    BusinessAnonymous,
    Unknown
}

export type IEventModifier<DataType> = Partial<DataType> | ((data: DataType) => Partial<DataType>);

export interface IEventProps {
    eventName: string;
    shortEventName: string;
    critical?: boolean;
    isEventTypePrefixingDisabled?: boolean;
    requiresParent?: boolean;
    samplingFeature?: {};
}

export interface IEventType<DataType = any> { // tslint:disable-line:no-any
    enabled(): boolean;
    isTypeOf(event: IEvent<DataType>): boolean;
    /**
     * Gets a version of this event type specific to the given private manager.
     * Events logged using the sub-version will go only to handlers registered with the given manager.
     * @param manager A specific private manager to which to log events.
     */
    withManager(manager: IManager): this;
    withData(modifier: IEventModifier<DataType>): this;
    /**
     * Gets a version of this event type that has been extended with more context.
     * All events fired by the new type will have the specified context, plus context
     * inherited from its parent.
     * @param context A set of values to add to all derived events.
     */
    withContext(modifier: IEventModifier<IEventContext>): this;
    from(sourceEventType?: this): this;
}

export function addEventProps(
    eventPrototype: EventBase<{}, {}>,
    props: IEventProps,
    metadata: IEventMetadataDeclarationTable,
    baseClass?: { prototype: { metadata: IEventMetadataTable } }): void {
    assign(eventPrototype, props);

    const resultMetadata: IEventMetadataTable = <IEventMetadataTable>{};
    for (const key in metadata) {
        if (Object.prototype.hasOwnProperty.call(metadata, key)) {
            let item = metadata[key];
            const result = resultMetadata[key] = <IEventMetadata>(typeof item === 'number' ? { type: item } : item);
            result.definedInName = props.shortEventName;
            if (result.type === EventFieldType.Number) {
                result.isMetric = true;
            }
        }
    }

    eventPrototype.metadata = baseClass ? {
        ...baseClass.prototype.metadata,
        ...resultMetadata
    } : resultMetadata;
}

type ILoggableValue = string | number | boolean | undefined | ILoggableObject;
type ILoggableObject = { [key: string]: ILoggableValue };

const managerSpecificEventTypes = new WeakMap<IManager, WeakMap<{}, any>>(); // tslint:disable-line:no-any

export class EventBase<
    StartDataType extends { startTimeOverride?: number },
    DataType extends StartDataType> implements IEvent, IEventProps {
    public manager!: IManager;
    public readonly id: number;
    public readonly isEventTypePrefixingDisabled!: boolean;
    public readonly parentId!: number;
    public eventName!: string;
    public shortEventName!: string;
    public readonly startTime: number;
    public endTime!: number;
    public readonly enabled: boolean;
    public critical!: boolean;
    public readonly vector: CorrelationVector;
    public readonly data!: DataType;
    public validationErrors: number;
    public metadata!: IEventMetadataTable;
    public requiresParent!: boolean;
    public readonly samplingFeature!: {};
    public readonly context: IEventContext = {};

    /**
     * This will return true if the event is enabled
     */
    public static enabled(this: { prototype: EventBase<{}, {}> }): boolean {
        return this.prototype._isEnabled();
    }

    /**
     * This will return true if the event is of this type
     * @param event {IEvent} The event to compare
     */
    public static isTypeOf(this: { prototype: IEvent }, event: IEvent): boolean {
        return event.eventName.indexOf(this.prototype.shortEventName + ',') >= 0;
    }

    public static withManager(manager: IManager): any { // tslint:disable-line:no-any
        if (!manager || manager === Manager) {
            return this;
        }

        let eventTypesForManager = managerSpecificEventTypes.get(manager);

        if (!eventTypesForManager) {
            eventTypesForManager = new WeakMap();
            managerSpecificEventTypes.set(manager, eventTypesForManager);
        }

        let eventType = eventTypesForManager.get(this);

        if (eventType) {
            return eventType;
        }

        // @ts-ignore
        class EventWithManager extends (this) {
        }

        // @ts-ignore
        EventWithManager.prototype.manager = manager;

        eventType = EventWithManager;

        eventTypesForManager.set(this, eventType);

        return eventType;
    }

    public static withData(modifier: IEventModifier<any>): any { // tslint:disable-line:no-any
        if (!modifier) {
            return this;
        }

        const addData = typeof modifier !== 'function' ? (data: any) => ({ // tslint:disable-line:no-any
            ...modifier,
            ...data
        }) : modifier;

        // @ts-ignore
        class EventWithData extends (this) {
            protected addData(data: any): any { // tslint:disable-line:no-any
                const localData = {
                    ...data,
                    ...addData(data)
                };

                return {
                    ...localData,
                    ...super.addData(localData)
                };
            }
        }

        return EventWithData;
    }

    public static withContext(modifier: IEventModifier<IEventContext>): any { // tslint:disable-line:no-any max-line-length
        if (!modifier) {
            return this;
        }

        const addContext = typeof modifier !== 'function' ? (context: IEventContext) => ({
            ...modifier,
            ...context
        }) : modifier;

        // @ts-ignore
        class EventWithContext extends (this) {
            protected addContext(context: IEventContext): IEventContext {
                const localContext = {
                    ...context,
                    ...addContext(context)
                };

                return {
                    ...localContext,
                    ...super.addContext(localContext)
                };
            }
        }

        return EventWithContext;
    }

    public static from(sourceEventType: any): any { // tslint:disable-line:no-any
        if (!sourceEventType) {
            return this;
        }

        return this
            .withManager(sourceEventType.prototype.manager)
            .withContext((context: IEventContext) => sourceEventType.prototype.addContext(context))
            .withData((data: any) => sourceEventType.prototype.addData(data)); // tslint:disable-line:no-any
    }

    public addContext(context: IEventContext): Partial<IEventContext> {
        return copyObject(this.manager, context, {}) as Partial<IEventContext>;
    }

    public addData(data: DataType): Partial<DataType> {
        return data;
    }

    protected constructor(data: StartDataType | undefined, startType: ClonedEventType, parent?: IEvent) {
        this.validationErrors = 0;
        this.data = {} as DataType;

        this.context = {
            ...{},
            ...this.addContext({})
        };

        this.id = _id++;

        this.enabled = this._isEnabled();
        // Set the parent id if needed
        if (parent) {
            this.parentId = parent.id;
        } else if (this.requiresParent) {
            this._addValidationError(ValidationErrorType.NoParent);
        }

        // Set the start time
        this.startTime = (data && data.startTimeOverride) || Manager.getTime();

        this.vector = new CorrelationVector(parent ? parent.vector : CorrelationVector.RootVector);

        // Set the data if we have it
        if (data) {
            this._setData(data);
        }

        // Send the start event
        this._logEvent(startType);
    }

    protected _isEnabled(): boolean {
        // All events are enabled by default
        return !this.samplingFeature || Manager.isFeatureEnabled(this.samplingFeature);
    }

    protected _setData(data: {}): void {
        const stored = this.data;
        for (const key of <string[]>Object.keys(data)) {
            if (key in this.metadata) {
                processValue(this.manager, data, stored as ILoggableObject, key);
            }
        }
        assign(this.data, this.addData(this.data));
    }

    protected _logEvent(eventType: ClonedEventType): void {
        this.manager.logEvent(this, eventType);
    }

    private _addValidationError(type: ValidationErrorType): void {
        this.validationErrors = this.validationErrors | type;
        this.manager.logValidationError(this, type);
    }
}

function processValue(
    manager: Pick<IManager,
        'cleanString'>,
    source: ILoggableObject,
    target: ILoggableObject,
    key: string
): void {
    const value = source[key];
    switch (typeof value) {
        case 'string':
            // Allow the cleaner to know what it is being stored as
            // This might be used to always scrub values for a particular key
            const cleaned = manager.cleanString(value as string, key);
            if (cleaned !== undefined) {
                target[key] = cleaned;
            }
            return;
        case 'undefined':
            return;
        case 'object':
            // Need to handle ABExperiment objects
            if (value && Object.getPrototypeOf(value) === Object.prototype) {
                // Still overwrite
                target[key] = copyObject(manager, value as ILoggableObject, {});
            } else {
                target[key] = value;
            }
            return;
        default:
            target[key] = value;
            return;
    }
}

function copyObject(
    manager: Pick<IManager, 'cleanString'>,
    source: ILoggableObject,
    target: ILoggableObject
): ILoggableObject {
    for (const key of Object.keys(source)) {
        processValue(manager, source, target, key);
    }
    return target;
}

const proto = EventBase.prototype;
proto.eventName = 'EventBase,';
proto.shortEventName = 'EventBase';
// All events are not critical by default
proto.critical = false;
// All events require parents by default
proto.requiresParent = true;
proto.manager = Manager;
